import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { VIDEOCALL_QA_BASEURL } from "../../../Api/api-constant";
import { Badge, Button, Callout, Card, Field, Page, PageHeader, Select, TextArea, TextInput } from "../../../ui";
import "./videoCallQaReview.scss";

/**
 * Class QA · Parameters & Prompts. Edit what each QA parameter checks and how it is prompted,
 * add new parameters, try a version on a class (the report is not touched), and activate it.
 * Versions are immutable: saving always makes a new inactive version. Activating needs the
 * reviewer's name (Academics for academic/behaviour, Support for technical) and only affects
 * classes analysed from then on. Talks to artium-videocall-qa /v1/rubrics*.
 */

type Pillar = "academic" | "behaviour" | "technical";
interface VersionRow {
  id: string; version: string; is_active: boolean; scorer_type: string;
  created_by: string | null; created_at: string; changelog: string | null;
}
interface Param {
  parameter_key: string; pillar: Pillar; title: string; scorer_type: string; checks: number;
  active_version: string | null; latest_version: string; reviewer: string | null; custom_code: boolean;
  versions: VersionRow[];
}
interface CheckItem {
  key: string; label?: string; question: string; metric?: string; pass_share?: number;
  skip_when?: Record<string, string>; [extra: string]: unknown;
}
interface Draft {
  parameter_key: string; pillar: Pillar; scorer_type: string; title: string; description: string;
  scale: string; model: string; prompt: string; levels: Record<string, string>;
  checklist: CheckItem[]; paramsJson: string; few_shot: unknown[];
}
interface TrialCheck { key: string; label: string; answer: string | null; met: boolean | null; note: string | null; t_s: number | null; frame_url: string | null }
interface Trial {
  id: string; status: string; error: string | null; elapsed_s: number | null; class_schedule_id: string | null;
  score: number | null; scale: string | null; summary: string | null; missing_elements: string[];
  checks: TrialCheck[]; evidence: { t_start: number; speaker?: string | null; quote?: string | null }[]; tokens: number;
}

const api = (path: string) => `${VIDEOCALL_QA_BASEURL}${path}`;
const errText = (e: any) => {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? d : d ? JSON.stringify(d) : e?.message || "Request failed";
};
const fmtT = (s?: number | null) => {
  const v = Math.max(0, Math.round(s || 0));
  return `${Math.floor(v / 60)}:${String(v % 60).padStart(2, "0")}`;
};
const TYPE_LABEL: Record<string, string> = {
  llm: "Transcript (LLM)", vlm: "Video frames (VLM)", code: "Computed (code)", hybrid: "Hybrid",
};
const EMPTY_ITEM: CheckItem = { key: "", label: "", question: "" };

function toDraft(cfg: any): Draft {
  const { checklist, ...rest } = cfg.params || {};
  return {
    parameter_key: cfg.parameter_key, pillar: cfg.pillar, scorer_type: cfg.scorer_type,
    title: cfg.title || "", description: cfg.description || "", scale: cfg.scale || "1-5",
    model: cfg.model || "", prompt: cfg.prompt || "", levels: cfg.levels || {},
    checklist: Array.isArray(checklist) ? checklist : [],
    paramsJson: JSON.stringify(rest, null, 2), few_shot: cfg.few_shot || [],
  };
}

function fromDraft(d: Draft): Record<string, unknown> {
  const extra = d.paramsJson.trim() ? JSON.parse(d.paramsJson) : {};
  const params: Record<string, unknown> = { ...extra };
  if (d.checklist.length) {
    params.checklist = d.checklist.map((c) => {
      const out: Record<string, unknown> = {};
      Object.entries(c).forEach(([k, v]) => {
        if (v !== "" && v !== undefined && v !== null) out[k] = v;
      });
      return out;
    });
  }
  return {
    parameter_key: d.parameter_key.trim(), pillar: d.pillar, scorer_type: d.scorer_type,
    title: d.title.trim(), description: d.description, scale: d.scale, model: d.model.trim() || null,
    prompt: d.prompt || null, levels: d.levels, params, few_shot: d.few_shot,
  };
}

const VideoCallQaRubrics: React.FC = () => {
  const [search, setSearch] = useSearchParams();
  const [params, setParams] = useState<Param[]>([]);
  const [selKey, setSelKey] = useState<string | null>(search.get("param"));
  const [selVersion, setSelVersion] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [changelog, setChangelog] = useState("");
  const [msg, setMsg] = useState<{ tone: "info" | "success" | "danger" | "warning"; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [classes, setClasses] = useState<{ class_schedule_id: string; instrument: string | null; call_duration_s?: number | null }[]>([]);
  const [trialClass, setTrialClass] = useState("");
  const [trial, setTrial] = useState<Trial | null>(null);
  const [activating, setActivating] = useState(false);
  const [reviewedBy, setReviewedBy] = useState("");
  const [activateNote, setActivateNote] = useState("");
  const poll = useRef<number | null>(null);

  const selected = useMemo(() => params.find((p) => p.parameter_key === selKey) || null, [params, selKey]);
  const version = useMemo(
    () => selected?.versions.find((v) => v.version === selVersion) || null,
    [selected, selVersion]
  );

  const loadParams = useCallback(async () => {
    try {
      const { data } = await axios.get<Param[]>(api("/v1/rubrics"));
      setParams(data);
      return data;
    } catch (e) {
      setMsg({ tone: "danger", text: errText(e) });
      return [];
    }
  }, []);

  useEffect(() => {
    loadParams();
    axios
      .get(api("/v1/classes"), { params: { limit: 300 } })
      .then(({ data }) => setClasses((data || []).filter((c: any) => c.call_duration_s)))
      .catch(() => undefined);
    return () => {
      if (poll.current) window.clearInterval(poll.current);
    };
  }, [loadParams]);

  // Load the chosen version into the editor (default: the latest).
  useEffect(() => {
    if (!selected || isNew) return;
    const v = selVersion && selected.versions.some((x) => x.version === selVersion) ? selVersion : selected.latest_version;
    if (v !== selVersion) {
      setSelVersion(v);
      return;
    }
    axios
      .get(api(`/v1/rubrics/${selected.parameter_key}/versions/${v}`))
      .then(({ data }) => setDraft(toDraft(data.config)))
      .catch((e) => setMsg({ tone: "danger", text: errText(e) }));
  }, [selected, selVersion, isNew]);

  const pick = (key: string) => {
    setIsNew(false);
    setSelKey(key);
    setSelVersion(null);
    setTrial(null);
    setActivating(false);
    setChangelog("");
    setMsg(null);
    setSearch({ param: key });
  };

  const startNew = () => {
    setIsNew(true);
    setSelKey(null);
    setSelVersion(null);
    setTrial(null);
    setChangelog("New parameter");
    setDraft({
      parameter_key: "", pillar: "behaviour", scorer_type: "llm", title: "", description: "", scale: "pct",
      model: "", prompt: "", levels: {}, checklist: [{ ...EMPTY_ITEM }], paramsJson: "{}", few_shot: [],
    });
  };

  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft((d) => (d ? { ...d, [k]: v } : d));
  const setItem = (i: number, patch: Partial<CheckItem>) =>
    setDraft((d) => (d ? { ...d, checklist: d.checklist.map((c, j) => (j === i ? { ...c, ...patch } : c)) } : d));
  const moveItem = (i: number, dir: -1 | 1) =>
    setDraft((d) => {
      if (!d) return d;
      const list = [...d.checklist];
      const j = i + dir;
      if (j < 0 || j >= list.length) return d;
      [list[i], list[j]] = [list[j], list[i]];
      return { ...d, checklist: list };
    });

  const save = async () => {
    if (!draft) return;
    let config: Record<string, unknown>;
    try {
      config = fromDraft(draft);
    } catch {
      setMsg({ tone: "danger", text: "Other settings must be valid JSON." });
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      const { data } = await axios.post(api("/v1/rubrics/versions"), { config, changelog });
      await loadParams();
      setIsNew(false);
      setSelKey(data.parameter_key);
      setSelVersion(data.version);
      setSearch({ param: data.parameter_key });
      setChangelog("");
      setMsg({ tone: "success", text: `Saved ${data.parameter_key} ${data.version} (not active yet). Try it on a class, then activate.` });
    } catch (e) {
      setMsg({ tone: "danger", text: errText(e) });
    } finally {
      setBusy(false);
    }
  };

  const tryIt = async () => {
    if (!selected || !selVersion || !trialClass) return;
    setBusy(true);
    setTrial(null);
    try {
      const { data } = await axios.post(api(`/v1/rubrics/${selected.parameter_key}/versions/${selVersion}/try`), {
        class_key: trialClass,
      });
      setTrial({ ...data, checks: [], evidence: [], missing_elements: [] } as Trial);
      if (poll.current) window.clearInterval(poll.current);
      poll.current = window.setInterval(async () => {
        try {
          const r = await axios.get<Trial>(api(`/v1/rubrics/trials/${data.id}`));
          setTrial(r.data);
          if (r.data.status === "done" || r.data.status === "failed") {
            if (poll.current) window.clearInterval(poll.current);
            setBusy(false);
          }
        } catch (e) {
          if (poll.current) window.clearInterval(poll.current);
          setBusy(false);
          setMsg({ tone: "danger", text: errText(e) });
        }
      }, 3000);
    } catch (e) {
      setBusy(false);
      setMsg({ tone: "danger", text: errText(e) });
    }
  };

  const activate = async () => {
    if (!selected || !selVersion) return;
    setBusy(true);
    try {
      await axios.post(api(`/v1/rubrics/${selected.parameter_key}/versions/${selVersion}/activate`), {
        reviewed_by: reviewedBy, note: activateNote || null,
      });
      await loadParams();
      setActivating(false);
      setActivateNote("");
      setMsg({ tone: "success", text: `${selected.parameter_key} ${selVersion} is active. It applies to classes analysed from now on (use Re-analyse on the class list to re-score one).` });
    } catch (e) {
      setMsg({ tone: "danger", text: errText(e) });
    } finally {
      setBusy(false);
    }
  };

  const isChecklist = !!draft && (draft.scorer_type === "vlm" || draft.checklist.length > 0);
  const codeOnly = !!draft && (draft.scorer_type === "code" || draft.scorer_type === "hybrid");
  const grouped = useMemo(() => {
    const g: Record<string, Param[]> = {};
    params.forEach((p) => (g[p.pillar] = [...(g[p.pillar] || []), p]));
    return g;
  }, [params]);

  return (
    <Page>
      <PageHeader
        eyebrow="Class QA"
        title="Parameters & Prompts"
        subtitle="Edit what each parameter checks and how it is prompted, try it on a class, then activate it. Saving makes a new version; nothing is re-scored automatically."
        actions={<Button onClick={startNew}>New parameter</Button>}
      />
      {msg && <Callout tone={msg.tone}>{msg.text}</Callout>}

      <div className="vcqa-rubrics">
        <Card title="Parameters" flush>
          {(["academic", "behaviour", "technical"] as Pillar[]).map((pillar) =>
            grouped[pillar] ? (
              <div key={pillar}>
                <div className="vcqa-rubrics__group">{pillar}</div>
                {grouped[pillar].map((p) => (
                  <button
                    type="button"
                    key={p.parameter_key}
                    className={`vcqa-rubrics__item ${p.parameter_key === selKey && !isNew ? "is-on" : ""}`}
                    onClick={() => pick(p.parameter_key)}
                  >
                    <b>{p.title}</b>
                    <span className="vcqa-small vcqa-muted">
                      {TYPE_LABEL[p.scorer_type] || p.scorer_type}
                      {p.checks ? ` · ${p.checks} checks` : ""}
                    </span>
                    <span className="vcqa-small">
                      {p.active_version ? <Badge tone="success">active {p.active_version}</Badge> : <Badge tone="warning">not active</Badge>}
                      {p.latest_version !== p.active_version && <Badge tone="info">draft {p.latest_version}</Badge>}
                    </span>
                  </button>
                ))}
              </div>
            ) : null
          )}
        </Card>

        <div className="vcqa-rubrics__main">
          {!draft && <Card><p className="vcqa-muted">Pick a parameter on the left, or create a new one.</p></Card>}

          {draft && (
            <>
              {selected && !isNew && (
                <Card
                  title={`${selected.title}`}
                  subtitle={`${selected.parameter_key} · ${selected.pillar} · ${TYPE_LABEL[selected.scorer_type] || selected.scorer_type} · reviewed by ${selected.reviewer || "–"}`}
                  actions={
                    <Select
                      size="sm"
                      value={selVersion || ""}
                      onChange={(e) => {
                        setSelVersion(e.target.value);
                        setTrial(null);
                        setActivating(false);
                      }}
                      options={selected.versions.map((v) => ({
                        value: v.version,
                        label: `${v.version}${v.is_active ? " (active)" : ""}`,
                      }))}
                    />
                  }
                >
                  {version && (
                    <div className="vcqa-small vcqa-muted">
                      Saved by {version.created_by || "–"} on {new Date(version.created_at).toLocaleString()}
                      {version.changelog && <pre className="vcqa-rubrics__log">{version.changelog}</pre>}
                    </div>
                  )}
                  <div className="vcqa-rubrics__actions">
                    <Select
                      size="sm"
                      value={trialClass}
                      onChange={(e) => setTrialClass(e.target.value)}
                      options={[
                        { value: "", label: "Choose a class to try on…" },
                        ...classes.map((c) => ({
                          value: c.class_schedule_id,
                          label: `${c.class_schedule_id} · ${c.instrument || "–"} · ${Math.round((c.call_duration_s || 0) / 60)} min`,
                        })),
                      ]}
                    />
                    <Button variant="secondary" size="sm" onClick={tryIt} disabled={!trialClass || busy}>
                      Try {selVersion} on this class
                    </Button>
                    {version && !version.is_active && (
                      <Button size="sm" onClick={() => setActivating((a) => !a)} disabled={busy}>
                        Activate {selVersion}
                      </Button>
                    )}
                  </div>
                  {activating && (
                    <div className="vcqa-rubrics__activate">
                      <Callout tone="warning">
                        {selected.pillar === "technical" ? "Support" : "Academics"} must review rubric changes before they go live.
                      </Callout>
                      <Field label="Reviewed by" required>
                        {(p) => <TextInput {...p} value={reviewedBy} onChange={(e) => setReviewedBy(e.target.value)} placeholder="Name (team)" />}
                      </Field>
                      <Field label="Note">
                        {(p) => <TextInput {...p} value={activateNote} onChange={(e) => setActivateNote(e.target.value)} />}
                      </Field>
                      <Button onClick={activate} disabled={reviewedBy.trim().length < 2 || busy}>Confirm activation</Button>
                    </div>
                  )}
                </Card>
              )}

              {trial && (
                <Card
                  title={`Trial on class ${trial.class_schedule_id || ""}`}
                  subtitle={trial.status === "done" ? `${trial.elapsed_s ?? "–"} s · ${trial.tokens} tokens · report not changed` : undefined}
                  actions={<Badge tone={trial.status === "done" ? "success" : trial.status === "failed" ? "danger" : "warning"}>{trial.status}</Badge>}
                >
                  {trial.error && <Callout tone="danger">{trial.error}</Callout>}
                  {trial.status === "done" && (
                    <>
                      <p>
                        <b>{trial.score === null ? "–" : trial.scale === "1-5" ? `${trial.score} / 5` : `${Math.round(trial.score)}%`}</b>{" "}
                        <span className="vcqa-muted">{trial.summary}</span>
                      </p>
                      {trial.checks.length > 0 ? (
                        <div className="vcqa-checks">
                          {trial.checks.map((c) => (
                            <div key={c.key} className={`vcqa-check ${c.met === true ? "is-ok" : c.met === false ? "is-high" : "is-unknown"}`}>
                              {c.frame_url ? (
                                <a href={c.frame_url} target="_blank" rel="noreferrer"><img src={c.frame_url} alt="" loading="lazy" /></a>
                              ) : (
                                <div className="vcqa-check__noimg" />
                              )}
                              <div>
                                <div className="vcqa-check__label">{c.met === true ? "✓" : c.met === false ? "✗" : c.answer === "not_applicable" ? "–" : "?"} {c.label}</div>
                                {c.note && <div className="vcqa-small vcqa-muted">{c.note}</div>}
                                {c.t_s ? <div className="vcqa-small vcqa-muted">at {fmtT(c.t_s)}</div> : null}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <ul className="vcqa-small">
                          {trial.evidence.map((e, i) => (
                            <li key={i}>[{fmtT(e.t_start)}] {e.speaker ? `${e.speaker}: ` : ""}{e.quote}</li>
                          ))}
                        </ul>
                      )}
                    </>
                  )}
                </Card>
              )}

              <Card
                title={isNew ? "New parameter" : `Edit ${selVersion || ""} → save as a new version`}
                subtitle={codeOnly ? "This parameter is computed in code: you can change its thresholds under Other settings." : undefined}
              >
                <div className="vcqa-rubrics__form">
                  {isNew && (
                    <div className="vcqa-rubrics__row">
                      <Field label="Key" hint="lowercase_with_underscores, permanent" required>
                        {(p) => <TextInput {...p} value={draft.parameter_key} onChange={(e) => set("parameter_key", e.target.value)} placeholder="teacher_energy" />}
                      </Field>
                      <Field label="Pillar">
                        {(p) => (
                          <Select {...p} value={draft.pillar} onChange={(e) => set("pillar", e.target.value as Pillar)}
                            options={[{ value: "academic", label: "Academic" }, { value: "behaviour", label: "Behaviour" }, { value: "technical", label: "Technical" }]} />
                        )}
                      </Field>
                      <Field label="Judged from">
                        {(p) => (
                          <Select {...p} value={draft.scorer_type} onChange={(e) => set("scorer_type", e.target.value)}
                            options={[{ value: "llm", label: "Transcript (what was said)" }, { value: "vlm", label: "Teacher video frames (what is seen)" }]} />
                        )}
                      </Field>
                    </div>
                  )}
                  <div className="vcqa-rubrics__row">
                    <Field label="Title" required>
                      {(p) => <TextInput {...p} value={draft.title} onChange={(e) => set("title", e.target.value)} />}
                    </Field>
                    <Field label="Score scale">
                      {(p) => (
                        <Select {...p} value={draft.scale} onChange={(e) => set("scale", e.target.value)}
                          options={[{ value: "pct", label: "% of checks met" }, { value: "1-5", label: "1–5 levels" }, { value: "bool", label: "Pass / fail" }]} />
                      )}
                    </Field>
                    {!codeOnly && (
                      <Field label="Model" hint="blank = the default from Settings">
                        {(p) => <TextInput {...p} value={draft.model} onChange={(e) => set("model", e.target.value)} placeholder="default" />}
                      </Field>
                    )}
                  </div>
                  <Field label="Description" hint="What this parameter measures (shown to reviewers; also sent to the model for 1–5 parameters).">
                    {(p) => <TextArea {...p} rows={3} value={draft.description} onChange={(e) => set("description", e.target.value)} />}
                  </Field>
                  {!codeOnly && (
                    <Field label="Prompt" hint="Extra instructions added to the system prompt. Never put names or personal details here.">
                      {(p) => <TextArea {...p} rows={6} value={draft.prompt} onChange={(e) => set("prompt", e.target.value)} />}
                    </Field>
                  )}

                  {!codeOnly && (isChecklist || isNew) && (
                    <div>
                      <div className="vcqa-rubrics__subhead">
                        Checks <span className="vcqa-muted vcqa-small">each is a yes/no question; "yes" must mean the standard is met</span>
                      </div>
                      {draft.checklist.map((c, i) => (
                        <div key={i} className="vcqa-rubrics__check">
                          <div className="vcqa-rubrics__row">
                            <Field label="Key">
                              {(p) => <TextInput {...p} size="sm" value={c.key} onChange={(e) => setItem(i, { key: e.target.value })} placeholder="artium_jacket" />}
                            </Field>
                            <Field label="Label (shown in reports)">
                              {(p) => <TextInput {...p} size="sm" value={c.label || ""} onChange={(e) => setItem(i, { label: e.target.value })} />}
                            </Field>
                            {draft.scorer_type === "vlm" && (
                              <Field label="Also measure">
                                {(p) => (
                                  <Select {...p} size="sm" value={c.metric || ""} onChange={(e) => setItem(i, { metric: e.target.value || undefined })}
                                    options={[{ value: "", label: "—" }, { value: "lighting", label: "Face brightness" }, { value: "framing", label: "Frontal face size" }]} />
                                )}
                              </Field>
                            )}
                            {draft.scorer_type === "vlm" && (
                              <Field label="Frames that must pass" hint="1 = every frame">
                                {(p) => (
                                  <TextInput {...p} size="sm" numeric value={c.pass_share ?? ""} placeholder="0.6"
                                    onChange={(e) => setItem(i, { pass_share: e.target.value === "" ? undefined : Number(e.target.value) })} />
                                )}
                              </Field>
                            )}
                            {draft.scorer_type === "llm" && (
                              <label className="vcqa-rubrics__checkbox">
                                <input
                                  type="checkbox"
                                  checked={c.skip_when?.class_type === "demo"}
                                  onChange={(e) => setItem(i, { skip_when: e.target.checked ? { class_type: "demo" } : undefined })}
                                />{" "}
                                Skip in demo (first) classes
                              </label>
                            )}
                          </div>
                          <Field label="Question">
                            {(p) => <TextArea {...p} rows={2} value={c.question} onChange={(e) => setItem(i, { question: e.target.value })} />}
                          </Field>
                          <div className="vcqa-rubrics__checktools">
                            <Button size="sm" variant="ghost" onClick={() => moveItem(i, -1)} disabled={i === 0}>↑</Button>
                            <Button size="sm" variant="ghost" onClick={() => moveItem(i, 1)} disabled={i === draft.checklist.length - 1}>↓</Button>
                            <Button size="sm" variant="ghost" onClick={() => set("checklist", draft.checklist.filter((_, j) => j !== i))}>Remove</Button>
                          </div>
                        </div>
                      ))}
                      <Button size="sm" variant="secondary" onClick={() => set("checklist", [...draft.checklist, { ...EMPTY_ITEM }])}>
                        Add check
                      </Button>
                    </div>
                  )}

                  {!codeOnly && !isChecklist && !isNew && draft.scale === "1-5" && (
                    <div>
                      <div className="vcqa-rubrics__subhead">Levels</div>
                      {["1", "2", "3", "4", "5"].map((lv) => (
                        <Field key={lv} label={`Level ${lv}`}>
                          {(p) => <TextArea {...p} rows={2} value={draft.levels[lv] || ""} onChange={(e) => set("levels", { ...draft.levels, [lv]: e.target.value })} />}
                        </Field>
                      ))}
                    </div>
                  )}

                  <Field label="Other settings (JSON)" hint="Thresholds and scorer options, e.g. frames, image_detail, late_grace_s, camera_off_max_pct.">
                    {(p) => <TextArea {...p} rows={5} className="vcqa-mono" value={draft.paramsJson} onChange={(e) => set("paramsJson", e.target.value)} />}
                  </Field>
                  <Field label="What changed and why" required>
                    {(p) => <TextInput {...p} value={changelog} onChange={(e) => setChangelog(e.target.value)} placeholder="e.g. clearer jacket question" />}
                  </Field>
                  <div>
                    <Button onClick={save} loading={busy} disabled={busy || changelog.trim().length < 3 || !draft.title.trim()}>
                      Save as new version
                    </Button>{" "}
                    <span className="vcqa-small vcqa-muted">Saved versions stay inactive until you activate them.</span>
                  </div>
                </div>
              </Card>
            </>
          )}
        </div>
      </div>
    </Page>
  );
};

export default VideoCallQaRubrics;
